// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundInvestorsSummary } from '../services/fundsService.ts'

/**
 * useFundInvestorsSummary — fetches the Investors-tab summary block for one
 * fund (committed capital + investor count). One-shot fetch on mount; mirrors
 * the simpler shape of the existing dashboard fetches that don't need Redux
 * caching (the data is fund-tab-local). `refetch` re-reads it after the
 * Investors grid adds, edits or deletes a commitment.
 */
export function useFundInvestorsSummary(fundId: string | null | undefined) {
  const recordIdString = fundId ? String(fundId) : null

  const [data, setData] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasAttemptedFetch = useRef(false)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordIdString])

  useEffect(() => {
    if (!recordIdString) {
      setData(null)
      setLoading(false)
      setError(null)
      return
    }
    if (hasAttemptedFetch.current) return

    let cancelled = false
    hasAttemptedFetch.current = true
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await fetchFundInvestorsSummary(recordIdString, token)
        if (cancelled) return
        if (res.error) setError(res.error)
        setData(res.data ?? null)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load investors summary')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [recordIdString, version])

  const refetch = useCallback(() => {
    hasAttemptedFetch.current = false
    setVersion((v) => v + 1)
  }, [])

  return { data, loading, error, refetch }
}
